import { Box, Image, Text, Tooltip } from '@wix/design-system';
import {
  ECLIPSE_SALE_TEXT_COLOR,
  EllipsisForTrans,
  FREE_DOMAIN_PLAN_CARD_TRANSLATION_KEY,
  FREE_DOMAIN_PLAN_CARD_UP_TO_3_YEARS_TRANSLATION_KEY,
  useCurrentModule,
  useDomainIntent,
  useDomainItemDesign,
} from '@wix/package-picker-shared-eclipse';
import { CreativeOffer } from '@wix/premium-purchase-platform/@sdk';
import { Check, InfoCircleSmall } from '@wix/wix-ui-icons-common/lazy';
import React from 'react';
import s from './ProductCard.scss';

interface ProductCardFeatureRowWithDescriptionProps {
  row: CreativeOffer.MarketingFeature;
}

export const ProductCardFeatureRowWithDescription = ({
  row,
}: ProductCardFeatureRowWithDescriptionProps) => {
  const module = useCurrentModule();
  const { t, Trans } = module.internals.useTranslations();
  const { experiments } = module.internals.experiments.hooks.useExperiments();
  const { showDomain, domainName } = useDomainIntent();

  const handleFeatureTooltipHover = () => {
    module.internals.bi.onFeatureTooltipHover?.({
      featureKey: row.featureName ?? undefined,
    });
  };

  const isDomainRow =
    row.content?.localized?.translationKey ===
    FREE_DOMAIN_PLAN_CARD_TRANSLATION_KEY;
  const showDomainItemDesign = useDomainItemDesign(isDomainRow);

  if (!row.content?.localized) {
    return null;
  }

  const { translationKey, tooltipKey, descriptionKey } = row.content?.localized;
  const iconUrl = row.presentation?.style?.icon?.url;

  const transTags = {
    b: <b />,
    ellipsis: <EllipsisForTrans maxWidth={160} />,
  };

  const transParams = { domainName: showDomain ? domainName : undefined };

  const displayTranslationKey =
    isDomainRow &&
    experiments.isEnabled('matchDomainCycleToPremiumCycle')
      ? FREE_DOMAIN_PLAN_CARD_UP_TO_3_YEARS_TRANSLATION_KEY
      : translationKey;

  return (
    <Box
      direction="horizontal"
      width="100%"
      gap="4px"
      minHeight="21px"
      verticalAlign="top"
      className={showDomainItemDesign ? s.domainFeatureChip : undefined}
    >
      <Box height="18px" verticalAlign="middle" marginRight="4px">
        <Check
          color={showDomainItemDesign ? ECLIPSE_SALE_TEXT_COLOR : undefined}
        />
      </Box>
      <Box direction="vertical" gap="2px" width="100%">
        <Box direction="horizontal" gap="SP1" verticalAlign="middle">
          <Text
            key={row.featureName ?? ''}
            size="small"
            weight={showDomainItemDesign ? 'bold' : undefined}
            className={
              showDomainItemDesign ? s.domainFeatureChipText : undefined
            }
            style={
              showDomainItemDesign
                ? { color: ECLIPSE_SALE_TEXT_COLOR }
                : undefined
            }
          >
            <Trans
              i18nKey={displayTranslationKey}
              values={transParams}
              components={transTags}
            />
          </Text>
          {iconUrl ? (
            <Box inline position="relative" className={s.featureIcon}>
              <Image src={iconUrl} width="18px" height="18px" transparent />
            </Box>
          ) : null}
          {tooltipKey && (
            <Tooltip content={t(tooltipKey)} onShow={handleFeatureTooltipHover}>
              <InfoCircleSmall
                display="flex"
                color={
                  showDomainItemDesign
                    ? ECLIPSE_SALE_TEXT_COLOR
                    : 'var(--wsr-color-D40)'
                }
              />
            </Tooltip>
          )}
        </Box>
        {descriptionKey && (
          <Text size="tiny" secondary>
            <Trans
              i18nKey={descriptionKey}
              values={transParams}
              components={transTags}
            />
          </Text>
        )}
      </Box>
    </Box>
  );
};
